import React, { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';

type SecurityContextType = {
  canRead: (resource: string) => boolean;
  canCreate: (resource: string) => boolean;
  canUpdate: (resource: string, ownerId?: string) => boolean;
  canDelete: (resource: string) => boolean;
  isAdmin: boolean;
};

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export const SecurityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // The `resource` argument is part of the SecurityContextType signature but
  // is not needed by the current (permissive) implementation. It is omitted
  // here to avoid unused-parameter noise; callers may still pass it.
  const canRead = useCallback(() => {
    return true;
  }, []);

  const canCreate = useCallback(() => {
    return !!user;
  }, [user]);

  // `_resource` is the first positional parameter (matching the
  // SecurityContextType signature) so that `ownerId` stays at index 1.
  const canUpdate = useCallback((_resource: string, ownerId?: string) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return ownerId === user.id;
  }, [user]);

  const canDelete = useCallback(() => {
    return isAdmin;
  }, [isAdmin]);

  const contextValue = useMemo(() => ({
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    isAdmin,
  }), [canRead, canCreate, canUpdate, canDelete, isAdmin]);

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
    </SecurityContext.Provider>
  );
};

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
};